import { useEffect } from 'react'
import { useAuroraStore } from '../store/auroraStore'

/**
 * Applies the theme to the document root.
 *
 * Three states, matching how people actually think about it:
 *   'light' / 'dark' — an explicit choice, stamped as data-theme
 *   'system'         — no stamp; CSS media queries decide
 *
 * The meta theme-color tags in index.html already carry both, so the browser
 * chrome follows without extra work in the system case.
 */
export function useTheme() {
  const theme = useAuroraStore((s: any) => s.ui.theme ?? s.clientState?.theme ?? 'system')

  useEffect(() => {
    const root = document.documentElement
    if (theme === 'system') root.removeAttribute('data-theme')
    else root.setAttribute('data-theme', theme)
  }, [theme])

  // Keep the address-bar color in sync when following the system.
  useEffect(() => {
    if (theme !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const apply = () => {
      const meta = document.querySelector('meta[name="theme-color"]')
      if (meta) meta.setAttribute('content', mq.matches ? '#0F1216' : '#FDFBF7')
    }
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [theme])

  return theme as 'light' | 'dark' | 'system'
}

/** Resolves 'system' to the concrete theme currently in effect. */
export function useResolvedTheme(): 'light' | 'dark' {
  const theme = useTheme()
  if (theme !== 'system') return theme
  return typeof window !== 'undefined' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}
